import React, { useState, useCallback } from 'react';
import { Modal, Button, Spin, Alert, message, Tooltip } from 'antd';
import { DownloadOutlined, CloseOutlined, FullscreenOutlined, PrinterOutlined } from '@ant-design/icons';
import { useTheme } from 'hooks/useTheme';
import { useI18n } from 'hooks/useI18n';
import './pdf-modal.scss';

const PdfModal = ({
	visible = false,
	onClose,
	pdfUrl = '',
	title = '',
	loading = false,
	error = null,
	width = '90%',
	height = '80vh',
}) => {
	const { theme, themeColor } = useTheme();
	const { t } = useI18n();
	const [isDownloading, setIsDownloading] = useState(false);

	// Use translated title if no title provided
	const modalTitle = title || t('common.pdf_modal.title');

	const handleDownload = useCallback(async () => {
		if (!pdfUrl) return;
		
		try {
			setIsDownloading(true);
			const link = document.createElement('a');
			link.href = pdfUrl;
			link.download = `${modalTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
			link.target = '_blank';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			message.success(t('common.pdf_modal.download_success'));
		} catch (error) {
			message.error(t('common.pdf_modal.download_failed'));
		} finally {
			setIsDownloading(false);
		}
	}, [pdfUrl, modalTitle, t]);

	const handleFullscreen = useCallback(() => {
		if (pdfUrl) {
			window.open(pdfUrl, '_blank', 'noopener,noreferrer');
		}
	}, [pdfUrl]);

	const handlePrint = useCallback(() => {
		if (pdfUrl) {
			const printWindow = window.open(pdfUrl, '_blank', 'width=800,height=600');
			if (printWindow) {
				printWindow.onload = () => {
					setTimeout(() => {
						printWindow.print();
					}, 250);
				};
			}
		}
	}, [pdfUrl]);

	return (
		<Modal
			title={
				<div className="pdf-modal-header">
					<span className="pdf-modal-title">{modalTitle}</span>
					{/* <div className="pdf-modal-actions">
						<Tooltip title={t('common.pdf_modal.download')} placement="bottom">
							<Button
								type="text"
								icon={<DownloadOutlined />}
								onClick={handleDownload}
								disabled={!pdfUrl || loading}
								loading={isDownloading}
								className="pdf-action-btn"
							/>
						</Tooltip>
						<Tooltip title={t('common.pdf_modal.open_new_tab')} placement="bottom">
							<Button
								type="text"
								icon={<FullscreenOutlined />}
								onClick={handleFullscreen}
								disabled={!pdfUrl || loading}
								className="pdf-action-btn"
							/>
						</Tooltip>
						<Tooltip title={t('common.pdf_modal.print')} placement="bottom">
							<Button
								type="text"
								icon={<PrinterOutlined />}
								onClick={handlePrint}
								disabled={!pdfUrl || loading}
								className="pdf-action-btn"
							/>
						</Tooltip>
					</div> */}
				</div>
			}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={width}
			centered
			destroyOnClose
			className={`pdf-modal ${theme}-theme`}
			closeIcon={<CloseOutlined className="pdf-close-icon" />}
			styles={{
				body: {
					padding: 0,
					height: height,
					maxHeight: 'calc(100vh - 240px)',
				},
			}}
			maskStyle={{
				background: theme === 'dark' 
					? 'linear-gradient(135deg, rgba(30, 30, 30, 0.8) 0%, rgba(20, 20, 20, 0.9) 100%)'
					: 'linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%)',
				"-webkit-backdrop-filter": 'blur(8px)',
				backdropFilter: 'blur(8px)',
			}}>
			<div className="pdf-modal-content">
				{loading && (
					<div className="pdf-loading">
						<Spin size="large" />
						<p>{t('common.pdf_modal.loading')}</p>
					</div>
				)}

				{error && (
					<div className="pdf-error">
						<Alert
							message={t('common.pdf_modal.error.title')}
							description={error || t('common.pdf_modal.error.description')}
							type="error"
							showIcon
							action={
								<Button size="small" danger onClick={onClose}>
									{t('common.pdf_modal.error.close_button')}
								</Button>
							}
						/>
					</div>
				)}

				{!loading && !error && pdfUrl && (
					<div className="pdf-iframe-container">
						<iframe
							src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH&zoom=page-fit`}
							title={modalTitle}
							className="pdf-iframe"
							frameBorder="0"
							onLoad={() => {
								// Optional: You can add onLoad callback here
							}}
							onError={() => {
								console.error('Error loading PDF in iframe');
							}}
						/>
					</div>
				)}

				{!loading && !error && !pdfUrl && (
					<div className="pdf-no-content">
						<Alert
							message={t('common.pdf_modal.no_content.title')}
							description={t('common.pdf_modal.no_content.description')}
							type="warning"
							showIcon
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default PdfModal;